import { useEffect, useState } from "react";
import {
  GameClubMyRankBar,
  GameClubMyRankContent,
  GameClubMyRankContentInfo,
  GameClubMyRankHeader,
  GameClubMyRankWrapper,
} from "../Style/GameClubMyStyles";
import { GameClubDto, getClubUserState } from "../../../../api/game-club";
import { getBarWidth, getClubScore, getNextRank, getRank } from "./utils/rank";
import GameClubCalendar from "./component/GameClubCalendar";
import { LOCAL_STORAGE_ACCESS_KEY } from "../../../../utils/network";

interface GameClubMyRankProps {
  data: GameClubDto;
  setData: (data: GameClubDto) => void;
}

export const GameClubMyRank = ({ data, setData }: GameClubMyRankProps) => {
  const [handCount, setHandCount] = useState(0);
  const [handScore, setHandScore] = useState(0);
  const [bettingScore, setBettingScore] = useState(0);
  const [winScore, setWinScore] = useState(0);
  const [tournamentCount, setTournamentCount] = useState(0);
  const [tournamentWinCount, setTournamentWinCount] = useState(0);
  const [tournamentRake, setTournamentRake] = useState(0);
  const [showInfo, setShowInfo] = useState("");
  const [topHandCount, setTopHandCount] = useState(0);
  const [topRankingWinCount, setTopRankingWinCount] = useState(0);
  const [topTournamentJoinCount, setTopTournamentJoinCount] = useState(0);
  const [topTournamentWinCount, setTopTournamentWinCount] = useState(0);
  const [topRakeAmount, setTopRakeAmount] = useState(0);
  const [topBetAmount, setTopBetAmount] = useState(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  useEffect(() => {
    setHandCount(data.totalHandCount);
    setHandScore(data.totalHandCount);
    setBettingScore(data.totalBetAmount);
    setWinScore(data.totalRingWinCount);
    setTournamentCount(data.totalTournamentJoinCount);
    setTournamentWinCount(data.totalTournamentWinCount);
    setTournamentRake(data.totalRakeAmount);
    setTopHandCount(data.topTotalHandCount);
    setTopRankingWinCount(data.topTotalRingWinCount);
    setTopTournamentJoinCount(data.topTotalTournamentJoinCount);
    setTopTournamentWinCount(data.topTotalTournamentWinCount);
    setTopRakeAmount(data.topTotalRakeAmount);
    setTopBetAmount(data.topTotalBetAmount);
  }, [data]);

  const getUserData = async (
    startDate?: string | null,
    endDate?: string | null
  ) => {
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
    const accessToken = token ? token.replace("Bearer ", "") : "-";

    const res = await getClubUserState(accessToken, startDate, endDate);
    if (res.result === 1) {
      if (!("club" in res.payload)) {
        setData(res.payload);
      }
    }
  };

  useEffect(() => {
    if ((startDate && endDate) || (!startDate && !endDate)) {
      getUserData(startDate, endDate);
    }
  }, [startDate, endDate]);

  const handleStartDate = (date: string) => {
    setStartDate(date);
  };

  const handleEndDate = (date: string) => {
    setEndDate(date);
  };

  const rankItems = [
    {
      name: "총 핸드",
      count: handCount,
      score: handScore,
      message: "클럽에 가입한 회원들이 받은 총 핸드수",
      top: topHandCount,
    },
    {
      name: "베팅 GP",
      score: bettingScore,
      message: "클럽에 가입한 회원이 배팅한 GP 수량",
      top: topBetAmount,
    },
    {
      name: "총 승리",
      score: winScore,
      message:
        "클럽에 가입한 회원이 한번의 핸드를 받아서 게임을 진행했을때 위닝한 수",
      top: topRankingWinCount,
    },
    {
      name: "토너먼트 참여",
      score: tournamentCount,
      message: "클럽에 가입한 회원이 러너러너 토너먼트에 참여한 총 횟수",
      top: topTournamentJoinCount,
    },
    {
      name: "토너먼트 Trophy",
      score: tournamentWinCount,
      message: "클럽에 가입되어있는 유저가 토너먼트에서 우승한 총 횟수",
      top: topTournamentWinCount,
    },
    {
      name: "레이크",
      score: tournamentRake,
      message:
        "클럽에 가입한 회원이 테이블게임에 참여해 지불된 GP레이크(5%)의 합.",
      top: topRakeAmount,
    },
  ];

  return (
    <GameClubMyRankWrapper>
      <GameClubMyRankHeader>
        <div className="title">총합 랭킹</div>
        <div className="filter" onClick={() => setIsCalendarOpen(true)}>
          <img src="/image-web/game/club/filter.svg" alt="filter" />
        </div>
      </GameClubMyRankHeader>
      {rankItems.map(
        (item, index) =>
          item.score !== -1 && (
            <GameClubMyRankContent key={index}>
              <div className="header">
                <div className="name">
                  {item.name}{" "}
                  {item.count ? (
                    <>
                      {item.count.toLocaleString()}
                      <span> 회</span>
                    </>
                  ) : (
                    ""
                  )}
                </div>
                <img
                  src="image-web/game/club/rank_info.svg"
                  alt="info"
                  onClick={() =>
                    setShowInfo(showInfo === item.name ? "" : item.name)
                  }
                />
                {showInfo === item.name && (
                  <GameClubMyRankContentInfo>
                    <div className="message">{item.message}</div>
                  </GameClubMyRankContentInfo>
                )}
              </div>
              <div className="bottom">
                <GameClubMyRankBar bar={getBarWidth(item.score, item.name)}>
                  <div className="bar">
                    <div className="bar-color"></div>
                  </div>
                  <div className="score">
                    <div className="item">
                      현재 {getRank(item.score, item.name, item.top)}{" "}
                      {getClubScore(item.score)}
                    </div>
                    <div className="item">
                      <span>다음 등급까지 필요 </span>
                      {getClubScore(getNextRank(item.score, item.name))}
                    </div>
                  </div>
                </GameClubMyRankBar>
                <div className="tire-wrapper">
                  <img
                    src={`/image-web/game/tier/${getRank(
                      item.score,
                      item.name,
                      item.top
                    )}.png`}
                    alt={getRank(item.score, item.name, item.top)}
                  />
                  <div className="tire">
                    {getRank(item.score, item.name, item.top)}
                  </div>
                </div>
              </div>
            </GameClubMyRankContent>
          )
      )}
      {isCalendarOpen && (
        <GameClubCalendar
          startDate={startDate}
          endDate={endDate}
          handleStartDate={handleStartDate}
          handleEndDate={handleEndDate}
          onClose={() => setIsCalendarOpen(false)}
        />
      )}
    </GameClubMyRankWrapper>
  );
};
